import React, { SelectHTMLAttributes, useId } from 'react';
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons';
import clsx from 'clsx';

import { FieldProps } from '@typings';
import { getSlots } from '@utils';
import { fieldVariants } from '@utils/variants';

import { Field, Icon, Render, Slot } from '@components';

type Props = Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> &
  FieldProps & {
    placeholder?: string;
    options?: Array<
      {
        value: string | number;
        text: string | number;
        // Allow all standard HTML attributes for <option>
      } & React.OptionHTMLAttributes<HTMLOptionElement>
    >;
  };

export const FieldSelect = React.forwardRef<HTMLSelectElement, Props>(
  (
    {
      disabled,
      required,
      name,
      className,
      containerClassName,
      variant,
      labelVariant = 'floating',
      label,
      placeholder,
      showError = true,
      error,
      options,
      note,
      children,
      ...props
    },
    ref,
  ) => {
    const id = useId();

    const slots = getSlots<'note' | 'error' | 'icon'>(children);

    const getSelectItems = () => {
      if (options) {
        return options.map(({ value, text, ...attrs }) => (
          <option key={value} value={value} {...attrs}>
            {text}
          </option>
        ));
      }

      return slots.content;
    };

    return (
      <Field.Container className={containerClassName}>
        <Render if={slots.icon}>
          <div className="pointer-events-none absolute left-4 top-4 z-10">
            <Slot slot={slots.icon} />
          </div>
        </Render>

        <select
          {...props}
          className={fieldVariants({
            variant,
            labelVariant,
            error: !!error,
            label: !!label,
            icon: !!slots.icon,
            className,
          })}
          ref={ref}
          name={name}
          required={required}
          id={id}
          disabled={disabled}
        >
          {getSelectItems()}
        </select>
        <Field.Label
          variant={labelVariant}
          for={id}
          required={required}
          disabled={disabled}
          className={clsx('pl-[2px]', {
            'start-12': slots.icon,
          })}
        >
          {label}
        </Field.Label>
        <Icon
          icon={faCaretDown}
          className={clsx(
            'pointer-events-none absolute right-4 top-4 text-[16px] text-neural-04',
            { 'text-neural-02': disabled },
          )}
        />
        <Render if={showError}>
          <Slot slot={slots.error}>
            <Field.Error>{error}</Field.Error>
          </Slot>
        </Render>
        <Render if={!error}>
          <Slot slot={slots.note}>
            <Field.Note>{note}</Field.Note>
          </Slot>
        </Render>
      </Field.Container>
    );
  },
);

FieldSelect.displayName = 'Field.Select';
